import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { useDocumentTitle } from '../hooks/useDocumentTitle';

const PropelLogo = ({ className = 'w-8 h-8' }) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32" fill="none" className={className}>
    <path d="M6 26L14 6L17 15L20 6L28 26L20 21L17 26L14 21Z" fill="#4F46E5" opacity="0.95"/>
    <path d="M14 6L17 15L20 6L17 10Z" fill="#6366F1"/>
  </svg>
);

export default function TermsPage() {
  useDocumentTitle('Terms of Service - Propel');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="mb-10">
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 transition-colors mb-6">
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
          <div className="flex items-center gap-3 mb-4">
            <PropelLogo className="w-8 h-8" />
            <span className="text-xl font-heading font-bold text-gray-900">Propel</span>
          </div>
          <h1 className="text-3xl sm:text-4xl font-heading font-bold text-gray-900">Terms of Service</h1>
          <p className="mt-2 text-sm text-gray-500">Last updated: February 27, 2026</p>
        </div>

        {/* Content */}
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-8 sm:p-10 space-y-8">

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">1. Acceptance of Terms</h2>
            <p className="text-gray-600 leading-relaxed">
              By accessing or using Propel ("the Service"), you agree to be bound by these Terms of Service ("Terms"). If you are using the Service on behalf of an organization, you represent that you have the authority to bind that organization to these Terms. If you do not agree, you may not use the Service.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">2. Description of Service</h2>
            <p className="text-gray-600 leading-relaxed">
              Propel is a SaaS platform for managing Upwork job proposals. The Service includes:
            </p>
            <ul className="list-disc list-inside text-gray-600 leading-relaxed space-y-2 mt-3">
              <li>Automated syncing of Upwork job postings, proposals, and messages</li>
              <li>AI-powered cover letter generation using third-party language models</li>
              <li>Proposal workflow management with team review and approval</li>
              <li>Portfolio management and vector-based search</li>
              <li>Real-time notifications and analytics dashboards</li>
              <li>A Chrome browser extension for submitting proposals on Upwork</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">3. Account Responsibilities</h2>
            <ul className="list-disc list-inside text-gray-600 leading-relaxed space-y-2">
              <li>You must provide accurate and complete information when creating your account.</li>
              <li>You are responsible for maintaining the security of your password and account credentials.</li>
              <li>You must notify us immediately if you become aware of any unauthorized use of your account.</li>
              <li>Each individual may maintain only one account per tenant (organization).</li>
              <li>You are responsible for all activity that occurs under your account.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">4. Upwork Integration</h2>
            <p className="text-gray-600 leading-relaxed">
              By connecting your Upwork account to Propel, you authorize us to access your Upwork data via the Upwork API using OAuth. This includes reading job postings, proposals, messages, and profile information on your behalf.
            </p>
            <ul className="list-disc list-inside text-gray-600 leading-relaxed space-y-2 mt-3">
              <li>You remain solely responsible for compliance with Upwork's Terms of Service.</li>
              <li>Propel acts as an intermediary tool and does not guarantee any outcomes on the Upwork platform.</li>
              <li>You may disconnect your Upwork account at any time through your account settings.</li>
              <li>We are not affiliated with, endorsed by, or sponsored by Upwork Inc.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">5. AI-Generated Content</h2>
            <p className="text-gray-600 leading-relaxed">
              Propel uses third-party AI services (OpenAI and Anthropic) to generate cover letters and assist with search. By using these features:
            </p>
            <ul className="list-disc list-inside text-gray-600 leading-relaxed space-y-2 mt-3">
              <li>You acknowledge that AI-generated content is a suggestion and may contain errors or inaccuracies.</li>
              <li>You are solely responsible for reviewing, editing, and approving all content before submission to clients.</li>
              <li>Propel makes no guarantees about the quality, accuracy, or effectiveness of AI-generated content.</li>
              <li>Relevant job and portfolio data is shared with AI providers to generate personalized content. See our <Link to="/privacy" className="text-primary-600 hover:text-primary-700 underline">Privacy Policy</Link> for details.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">6. Acceptable Use</h2>
            <p className="text-gray-600 leading-relaxed">
              You agree not to:
            </p>
            <ul className="list-disc list-inside text-gray-600 leading-relaxed space-y-2 mt-3">
              <li>Use the Service for any unlawful purpose or in violation of Upwork's Terms of Service.</li>
              <li>Attempt to gain unauthorized access to other users' accounts or tenant data.</li>
              <li>Use automated scripts, bots, or scrapers to access the Service outside of normal use.</li>
              <li>Submit misleading, fraudulent, or spam proposals through the Service.</li>
              <li>Impersonate any person or misrepresent your affiliation with any entity.</li>
              <li>Reverse engineer, decompile, or attempt to extract the source code of the Service.</li>
              <li>Interfere with or disrupt the Service or its infrastructure.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">7. Intellectual Property</h2>
            <ul className="list-disc list-inside text-gray-600 leading-relaxed space-y-2">
              <li><strong>Your Content:</strong> You retain ownership of all proposals, cover letters, portfolio items, and other content you create or upload to the Service.</li>
              <li><strong>License to Us:</strong> By using the Service, you grant us a limited license to store, process, and display your content as necessary to provide the Service.</li>
              <li><strong>Our Platform:</strong> The Service, including its design, features, code, and branding, is owned by Propel and protected by intellectual property laws.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">8. Multi-Tenant Data</h2>
            <p className="text-gray-600 leading-relaxed">
              Propel operates on a multi-tenant architecture. Each organization (tenant) has logically isolated data. Within a tenant:
            </p>
            <ul className="list-disc list-inside text-gray-600 leading-relaxed space-y-2 mt-3">
              <li>Administrators can view and manage data for all users within their organization.</li>
              <li>Standard users can only access their own data and data shared with them through team features.</li>
              <li>Data from one tenant is never accessible to another tenant.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">9. Service Availability</h2>
            <p className="text-gray-600 leading-relaxed">
              We strive to keep the Service available at all times, but we do not guarantee uninterrupted access. The Service may be temporarily unavailable due to maintenance, updates, or factors beyond our control. We are not liable for any losses resulting from Service downtime.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">10. Termination</h2>
            <ul className="list-disc list-inside text-gray-600 leading-relaxed space-y-2">
              <li>You may close your account at any time by contacting us.</li>
              <li>We may suspend or terminate your account if you violate these Terms or engage in behavior that harms the Service or other users.</li>
              <li>Upon termination, your right to access the Service ceases immediately.</li>
              <li>You may request deletion of your data upon account closure. We will process deletion requests within 30 days, except where retention is required by law.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">11. Limitation of Liability</h2>
            <p className="text-gray-600 leading-relaxed">
              To the maximum extent permitted by law:
            </p>
            <ul className="list-disc list-inside text-gray-600 leading-relaxed space-y-2 mt-3">
              <li>The Service is provided "as is" and "as available" without warranties of any kind, express or implied.</li>
              <li>We do not guarantee that the Service will meet your specific requirements or achieve any particular results on Upwork.</li>
              <li>We are not liable for any indirect, incidental, special, consequential, or punitive damages arising from your use of the Service.</li>
              <li>Our total liability for any claim related to the Service shall not exceed the amount you paid for the Service in the 12 months preceding the claim.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">12. Changes to These Terms</h2>
            <p className="text-gray-600 leading-relaxed">
              We may update these Terms from time to time. If we make material changes, we will notify you by email or through a prominent notice on the Service at least 30 days before the changes take effect. Your continued use of the Service after the effective date constitutes acceptance of the updated Terms.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">13. Governing Law</h2>
            <p className="text-gray-600 leading-relaxed">
              These Terms are governed by and construed in accordance with applicable law. Any disputes arising from these Terms or your use of the Service will be resolved through good-faith negotiation. If negotiation fails, disputes will be submitted to binding arbitration.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">14. Contact Us</h2>
            <p className="text-gray-600 leading-relaxed">
              If you have questions about these Terms, please reach out to us on our <Link to="/contact" className="text-primary-600 hover:text-primary-700 underline">Contact page</Link>.
            </p>
          </section>

        </div>

        {/* Footer */}
        <div className="mt-8 flex items-center justify-between text-sm text-gray-400">
          <p>&copy; {new Date().getFullYear()} Propel. All rights reserved.</p>
          <div className="flex items-center gap-6">
            <Link to="/privacy" className="hover:text-gray-600 transition-colors">Privacy Policy</Link>
            <Link to="/contact" className="hover:text-gray-600 transition-colors">Contact</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
